// AI suggestion feedback vocabulary. Mirrors backend/services/ai/feedbackRules.js
// — keep the two in step: the server rejects any code not in REASON_CODES.
export const REASON_CODES = ['too_high', 'too_low', 'wrong_comps_or_data', 'holding_strategy', 'other'];

export const REASON_LABELS = {
  too_high: 'Too high',
  too_low: 'Too low',
  wrong_comps_or_data: 'Wrong comps / bad data',
  holding_strategy: 'We are holding on purpose',
  other: 'Other',
};

// Free-text reasons the old 6-second toast chips stored in dismiss_reason
export const LEGACY_REASON_MAP = { 'too low': 'too_low', 'too high': 'too_high', 'bad data': 'wrong_comps_or_data' };

// Any stored reason (code, legacy chip text, or free text) → display label
export const reasonLabel = (reason) => {
  if (reason == null || reason === '') return '';
  if (REASON_LABELS[reason]) return REASON_LABELS[reason];
  const legacy = LEGACY_REASON_MAP[String(reason).trim().toLowerCase()];
  return legacy ? REASON_LABELS[legacy] : String(reason);
};

const money = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
};

// "wrong · too high · you'd use $120" (the server's FeedbackView; any field may be absent)
export const feedbackLine = (fb) => {
  if (!fb || !fb.verdict) return '';
  const parts = [fb.verdict];
  if (fb.verdict === 'wrong' && fb.reasonCode) parts.push(reasonLabel(fb.reasonCode).toLowerCase());
  if (fb.preferredPrice != null && money(fb.preferredPrice)) parts.push(`you'd use ${money(fb.preferredPrice)}`);
  return parts.join(' · ');
};

// "Deterministic ladder: $118 (COMPS_UNDERCUT), band $59–$354 · AI $140 (+19%)".
// ladder = { recommended:{price,source,reason}|null, sanityBand:{lo,hi}|null, warnings } | null.
// Returns '' when there is no ladder (pre-ladder suggestions, or a re-analysis
// rather than a first price).
export const ladderLine = (ladder, aiPrice) => {
  if (!ladder || typeof ladder !== 'object') return '';
  const rec = ladder.recommended;
  const recPrice = Number(rec?.price);
  let s = 'Deterministic ladder: ';
  if (Number.isFinite(recPrice) && recPrice > 0) {
    s += money(recPrice);
    if (rec.reason) s += ` (${rec.reason})`;
  } else {
    s += `no basis${ladder.reason ? ` (${ladder.reason})` : ''}`;
  }
  const lo = Number(ladder.sanityBand?.lo), hi = Number(ladder.sanityBand?.hi);
  if (Number.isFinite(lo) && Number.isFinite(hi)) s += `, band ${money(lo)}–${money(hi)}`;
  const ai = Number(aiPrice);
  if (Number.isFinite(ai) && ai > 0 && Number.isFinite(recPrice) && recPrice > 0) {
    const pct = ((ai - recPrice) / recPrice) * 100;
    s += ` · AI ${money(ai)} (${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%)`;
  }
  if (Array.isArray(ladder.warnings) && ladder.warnings.length) s += ` · ${ladder.warnings.join(', ')}`;
  return s;
};

// Merge a fresh FeedbackView into a suggestion's feedback summary
// ({ mine, count, labelCount, latestLabel }) without waiting for the refetch
export const mergeFeedbackSummary = (summary, view) => {
  if (!view) return summary;
  const prevMine = summary?.mine || null;
  const wasLabel = !!prevMine?.isLabel;
  const isLabel = !!view.isLabel;
  return {
    ...(summary || {}),
    mine: view,
    count: (summary?.count ?? 0) + (prevMine ? 0 : 1),
    labelCount: Math.max(0, (summary?.labelCount ?? 0) + (isLabel && !wasLabel ? 1 : 0) - (!isLabel && wasLabel ? 1 : 0)),
    latestLabel: isLabel ? view : (summary?.latestLabel ?? null),
  };
};

// Parse an "I'd use $" input: '' → null (none), otherwise a finite price in
// (0, 100000) or NaN (invalid). Same bounds the server enforces.
export const parsePreferredPrice = (raw) => {
  const s = String(raw ?? '').replace(/[$,\s]/g, '');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 && n < 100000 ? Math.round(n * 100) / 100 : NaN;
};
